import React from 'react';
import { Mail, Phone, MapPin, Clock } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const Contact = () => {
  const contactInfo = [
    {
      icon: Mail,
      title: 'Email Us',
      content: 'info@geminitechlabs.com',
      link: 'mailto:info@geminitechlabs.com',
      description: 'Send us an email anytime'
    },
    {
      icon: Phone,
      title: 'Call Us',
      content: '+91 8097080066',
      link: 'tel:+918097080066',
      description: 'Mon-Fri from 9am to 6pm IST'
    },
    {
      icon: MapPin,
      title: 'Our Reach',
      content: 'Global Presence',
      link: null,
      description: 'US, Middle East & Southeast Asia'
    },
    {
      icon: Clock,
      title: 'Business Hours',
      content: '9:00 AM - 6:00 PM IST',
      link: null,
      description: 'Monday to Friday'
    }
  ];

  const offices = [
    {
      region: 'United States',
      focus: 'Automation & Integration Solutions',
      description: 'Serving businesses across North America'
    },
    {
      region: 'Middle East',
      focus: 'ERP Implementation & Data Migration',
      description: 'UAE, Saudi Arabia & GCC countries'
    },
    {
      region: 'Southeast Asia',
      focus: 'SME Digital Transformation',
      description: 'India, Singapore, Malaysia & Thailand'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-blue-600 via-blue-700 to-purple-700 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 bg-white rounded-full filter blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-white rounded-full filter blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Get in Touch</h1>
            <p className="text-xl text-blue-100">
              Let's discuss how we can help transform your business
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {contactInfo.map((info, index) => {
              const IconComponent = info.icon;
              return (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 text-center bg-gradient-to-br from-blue-50 to-white">
                  <CardContent className="p-8">
                    <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center mb-4 mx-auto">
                      <IconComponent className="h-7 w-7 text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{info.title}</h3>
                    {info.link ? (
                      <a href={info.link} className="text-blue-600 hover:text-blue-700 font-semibold block mb-1">
                        {info.content}
                      </a>
                    ) : (
                      <div className="text-gray-900 font-semibold mb-1">{info.content}</div>
                    )}
                    <p className="text-gray-600 text-sm">{info.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Main Contact Section */}
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Ready to Get Started?</h2>
              <p className="text-xl text-gray-600">
                Reach out to us via email or phone, and our team will get back to you within 24 hours
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <Card className="border-none shadow-xl bg-gradient-to-br from-blue-600 to-purple-700 text-white">
                <CardContent className="p-10">
                  <h3 className="text-2xl font-bold mb-4">For General Inquiries</h3>
                  <div className="space-y-4">
                    <div>
                      <div className="flex items-center mb-2">
                        <Mail className="h-5 w-5 mr-3" />
                        <span className="font-semibold">Email</span>
                      </div>
                      <a href="mailto:info@geminitechlabs.com" className="text-blue-100 hover:text-white transition-colors ml-8">
                        info@geminitechlabs.com
                      </a>
                    </div>
                    <div>
                      <div className="flex items-center mb-2">
                        <Phone className="h-5 w-5 mr-3" />
                        <span className="font-semibold">Phone</span>
                      </div>
                      <a href="tel:+918097080066" className="text-blue-100 hover:text-white transition-colors ml-8">
                        +91 8097080066
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-none shadow-xl bg-white">
                <CardContent className="p-10">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Response Time</h3>
                  <div className="space-y-4 text-gray-700">
                    <p className="flex items-start">
                      <span className="inline-block w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <span><strong>Email:</strong> Within 24 hours on business days</span>
                    </p>
                    <p className="flex items-start">
                      <span className="inline-block w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <span><strong>Phone:</strong> Immediate during business hours</span>
                    </p>
                    <p className="flex items-start">
                      <span className="inline-block w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <span><strong>Urgent:</strong> For urgent matters, please call directly</span>
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Global Offices Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Global Presence</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Serving clients across three continents with local expertise and global standards
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {offices.map((office, index) => (
              <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all bg-white">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mb-4 mx-auto">
                    <MapPin className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{office.region}</h3>
                  <div className="text-blue-600 font-semibold mb-2">{office.focus}</div>
                  <p className="text-gray-600 text-sm">{office.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Contact Us Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">What to Expect</h2>
            <div className="space-y-6">
              <div className="flex items-start p-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl">
                <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold mr-4 flex-shrink-0">1</div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Initial Consultation</h3>
                  <p className="text-gray-600">We'll discuss your business needs, challenges, and goals to understand how we can help</p>
                </div>
              </div>
              <div className="flex items-start p-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl">
                <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold mr-4 flex-shrink-0">2</div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Custom Proposal</h3>
                  <p className="text-gray-600">Receive a detailed proposal outlining our recommended solutions, timeline, and investment</p>
                </div>
              </div>
              <div className="flex items-start p-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl">
                <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold mr-4 flex-shrink-0">3</div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Implementation Planning</h3>
                  <p className="text-gray-600">Work together to plan the implementation, assign resources, and set clear milestones</p>
                </div>
              </div>
              <div className="flex items-start p-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl">
                <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold mr-4 flex-shrink-0">4</div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Ongoing Support</h3>
                  <p className="text-gray-600">Benefit from our continuous support, training, and optimization services</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-blue-600 via-blue-700 to-purple-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Let's Build Something Great Together</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-blue-100">
            Whether you have a question, need a quote, or want to discuss your project, we're here to help
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="mailto:info@geminitechlabs.com" className="inline-block">
              <button className="bg-white text-blue-600 hover:bg-gray-100 font-semibold text-lg px-8 py-4 rounded-lg shadow-xl hover:shadow-2xl transition-all">
                <Mail className="inline-block mr-2 h-5 w-5" />
                Email Us
              </button>
            </a>
            <a href="tel:+918097080066" className="inline-block">
              <button className="bg-white/10 backdrop-blur-sm border-2 border-white text-white hover:bg-white hover:text-blue-600 font-semibold text-lg px-8 py-4 rounded-lg transition-all">
                <Phone className="inline-block mr-2 h-5 w-5" />
                Call Us
              </button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;